// tabs/ZonesTab.tsx
import { useCallback, useEffect, useState } from 'react';
import { Select, Button, Group, Divider, SimpleGrid, Card, Text, ActionIcon, Flex } from '@mantine/core';
import { IconPlus, IconTrash, IconRefresh } from '@tabler/icons-react';
import Tooltip from '@/components/ui/overlays/Tooltip';
import { useCareZoneService, useACCounsellingServices, LeaderZoneView } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import ConfirmActionModal from './ConfirmActionModal';

interface ZonesTabProps {
  leaderId: string;
  active: boolean;
  onLeaderChange?: () => void;
}

export default function ZonesTab({ leaderId, active, onLeaderChange }: ZonesTabProps) {
  const CareZones = useCareZoneService();
  const ACCounsellingServices = useACCounsellingServices();

  const [leaderZones, setLeaderZones] = useState<LeaderZoneView[]>([]);
  const [allZones, setAllZones] = useState<any[]>([]);
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('Confirm Remove');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<(() => Promise<void>) | null>(null);

  const fetchLeaderZones = useCallback(async () => {
    if (!leaderId) return;
    setLoading(true);
    try {
      const result = await ACCounsellingServices.leader.getZones(leaderId);
      setLeaderZones(result);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [leaderId, ACCounsellingServices]);

  useEffect(() => {
    CareZones.getAll().then(setAllZones).catch(console.error);
  }, [CareZones]);

  useEffect(() => { if (active) fetchLeaderZones(); }, [active, fetchLeaderZones]);

  // leaderZones (LeaderZoneView) only carries zoneID/zoneName — the actual
  // description lives on the full lookup object from CareZones.getAll().
  const getZoneName = (zoneID: number) =>
    allZones.find((z) => z.zoneID === zoneID)?.name ?? `Zone ${zoneID}`;

  const getZoneDescription = (zoneID: number) =>
    allZones.find((z) => z.zoneID === zoneID)?.description ?? null;

  const handleAddZone = async () => {
    if (!selectedZoneId || !leaderId) return;
    const existing = leaderZones.map((z) => z.zoneID) ?? [];
    if (existing.includes(Number(selectedZoneId))) {
      toast.error('Duplicate', 'This zone is already assigned.');
      return;
    }
    setLoading(true);
    try {
      await ACCounsellingServices.leader.addZone(leaderId, { zoneID: Number(selectedZoneId) } as any);
      setSelectedZoneId(null);
      await fetchLeaderZones();
      onLeaderChange?.();
      toast.success('Zone Added', 'Zone assigned to leader.');
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to add zone.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveZone = (leaderZoneRowId: number, zoneName?: string) => {
    setConfirmTitle('Remove Zone');
    setConfirmMessage(`Remove this zone${zoneName ? ` (${zoneName})` : ''} from the leader?`);
    setConfirmAction(() => async () => {
      setLoading(true);
      try {
        await ACCounsellingServices.leader.removeZone(leaderId, leaderZoneRowId as any);
        await fetchLeaderZones();
        onLeaderChange?.();
        toast.warning('Zone Removed', 'Zone removed from leader.');
      } catch (e) {
        console.error(e);
        toast.error('Error', 'Failed to remove zone.');
      } finally {
        setLoading(false);
      }
    });
    setConfirmOpen(true);
  };

  return (
    <>
      <ConfirmActionModal
        opened={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        onConfirm={async () => { if (confirmAction) await confirmAction(); }}
        onClose={() => { setConfirmOpen(false); setConfirmAction(null); }}
      />
     

      <Group gap="sm" justify="space-between" mb="md">
        <Select
          placeholder="Select a zone to add"
          style={{ width: 300 }}
          data={allZones
            .filter((z) => !(leaderZones.map((lz) => lz.zoneID) ?? []).includes(z.zoneID))
            .map((z) => ({ value: String(z.zoneID), label: z.name ?? `Zone ${z.zoneID}` }))}
          value={selectedZoneId}
          onChange={setSelectedZoneId}
          searchable
        />
        <Group gap="sm">
         <Flex justify="right" align="center" >
        <Tooltip label="Refresh">
          <ActionIcon variant="subtle" onClick={fetchLeaderZones} loading={loading} size="lg">
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Flex>
        <Button leftSection={<IconPlus size={16} />} onClick={handleAddZone} disabled={!selectedZoneId} loading={loading}>
          Add Zone
        </Button>
        </Group>
      </Group>
      <Divider mb="md" />
      {leaderZones.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center">No zones assigned.</Text>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 3, }} spacing="md">
          {leaderZones.map((z: any) => {
            const description = getZoneDescription(z.zoneID);
            return (
              <Card key={z.leaderZoneID ?? z.zoneID} withBorder radius="md" padding="lg">
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <div>
                    <Text fw={700}>{z.zoneName ?? getZoneName(z.zoneID)}</Text>
                    {description && <Text size="sm" c="dimmed">{description}</Text>}
                  </div>
                  <ActionIcon
                    variant="light"
                    color="red"
                    onClick={() => handleRemoveZone(z.leaderZoneID ?? z.zoneID, z.zoneName ?? getZoneName(z.zoneID))}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Card>
            );
          })}
        </SimpleGrid>
      )}
    </>
  );
}